import { useCallback } from 'react';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';

import { DashboardScope } from '@api/domain/types';
import type { DashboardDrilldownFilter } from '@api/domain/types';
import { DashboardDrilldownTab, DashboardDrilldownType } from '@v2/pages/dashboard/common/types/drilldown';

import {
  DEFAULT_CHILD_SCOPE_BY_DRILLDOWN_TYPE,
  buildDashboardDrilldownFilter,
  isValidDashboardDrilldownTab,
  isValidDashboardScope,
} from '@v2/pages/dashboard/modules/drilldown/helpers';

export interface UseManageDashboardDrilldownTabbedBodyParams {
  drilldownType: DashboardDrilldownType;
  drilldownId: string;
}

export interface UseManageDashboardDrilldownTabbedBodyResult {
  tab: DashboardDrilldownTab;
  childScope: DashboardScope;
  drilldownFilter: DashboardDrilldownFilter;
  onSelectTab: (tab: DashboardDrilldownTab) => void;
}

export const useManageDashboardDrilldownTabbedBody = ({
  drilldownType,
  drilldownId,
}: UseManageDashboardDrilldownTabbedBodyParams): UseManageDashboardDrilldownTabbedBodyResult => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const params = (route.params ?? {}) as { tab?: unknown; childScope?: unknown };

  const tab: DashboardDrilldownTab = isValidDashboardDrilldownTab(params.tab) ? params.tab : DashboardDrilldownTab.List;

  const childScope = isValidDashboardScope(params.childScope) ? params.childScope : DEFAULT_CHILD_SCOPE_BY_DRILLDOWN_TYPE[drilldownType];

  const drilldownFilter = buildDashboardDrilldownFilter(drilldownType, drilldownId);

  const onSelectTab = useCallback(
    (next: DashboardDrilldownTab) => {
      navigation.setParams({ tab: next });
    },
    [navigation]
  );

  useFocusEffect(
    useCallback(() => {
      if (!isValidDashboardDrilldownTab(params.tab)) {
        navigation.setParams({ tab: DashboardDrilldownTab.List });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.tab])
  );

  return { tab, childScope, drilldownFilter, onSelectTab };
};
